/* =============================================================
   predictive-search.js
   -------------------------------------------------------------
   SKU search on top of Shopify's native search.

   The local SKU index is NOT used for rendering. It works as a
   query translator: it turns whatever the customer typed into
   the canonical SKU that Shopify is guaranteed to find.

       5084s1   ->  index  ->  5-08-4-s1
       5084z1   ->  index  ->  5-08-4-s1   (typo, Levenshtein)
       5-08-4-* ->  trim   ->  5-08-4      (similar products)

   Applied in two places:
     1. Dropdown       - canonical SKU is injected into the fetch
     2. /search page   - q is rewritten on form submit and on a
                         direct visit that rendered empty results

   Result markup stays native everywhere.
   ============================================================= */

const SKU_INDEX_CACHE_KEY = 'sku_index_v1';
const SKU_INDEX_TTL = 30 * 60 * 1000; // 30 minutes
const SKU_PAGE_SIZE = 250;
const SKU_MAX_PAGES = 8; // safety cap: up to 2000 products
const SKU_SIMILAR_TEXT_FALLBACK = 'No exact match. Similar products:';

class SkuIndex {
  constructor() {
    this.rows = null; // [{ n: '5084s1', sku: '5-08-4-s1' }]
    this.promise = null;
  }

  static normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9а-яёіїєґ]/gi, '');
  }

  static root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  static isSkuLike(term) {
    return /\d/.test(term) && !/\s/.test(term);
  }

  load() {
    if (this.rows) return Promise.resolve(this.rows);
    if (this.promise) return this.promise;

    const cached = this.readCache();
    if (cached) {
      this.rows = cached;
      return Promise.resolve(this.rows);
    }

    this.promise = this.fetchAll()
      .then((products) => {
        this.rows = this.build(products);
        this.writeCache(this.rows);
        return this.rows;
      })
      .catch((error) => {
        console.warn('[predictive-search] SKU index not built:', error);
        this.rows = []; // avoid hammering the network on every keystroke
        return this.rows;
      });

    return this.promise;
  }

  readCache() {
    try {
      const raw = sessionStorage.getItem(SKU_INDEX_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.rows || !parsed.rows.length) return null;
      if (Date.now() - parsed.t > SKU_INDEX_TTL) return null;
      return parsed.rows;
    } catch (e) {
      return null;
    }
  }

  writeCache(rows) {
    try {
      sessionStorage.setItem(SKU_INDEX_CACHE_KEY, JSON.stringify({ t: Date.now(), rows }));
    } catch (e) {
      /* quota exceeded or private mode - keep working without cache */
    }
  }

  fetchAll() {
    const all = [];
    const page = (n) =>
      fetch(`${SkuIndex.root()}products.json?limit=${SKU_PAGE_SIZE}&page=${n}`, {
        headers: { Accept: 'application/json' },
      })
        .then((r) => {
          if (!r.ok) throw new Error(`products.json ${r.status}`);
          return r.json();
        })
        .then((data) => {
          const list = (data && data.products) || [];
          all.push(...list);
          if (list.length === SKU_PAGE_SIZE && n < SKU_MAX_PAGES) return page(n + 1);
          return all;
        });

    return page(1);
  }

  build(products) {
    const seen = new Set();
    const rows = [];

    products.forEach((product) => {
      (product.variants || []).forEach((variant) => {
        if (!variant.sku) return;
        const n = SkuIndex.normalize(variant.sku);
        if (!n || seen.has(n)) return;
        seen.add(n);
        rows.push({ n, sku: variant.sku });
      });
    });

    return rows;
  }

  // Levenshtein distance with early exit
  static distance(a, b, max) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > max) return max + 1;

    let prev = new Array(b.length + 1);
    let cur = new Array(b.length + 1);

    for (let j = 0; j <= b.length; j++) prev[j] = j;

    for (let i = 1; i <= a.length; i++) {
      cur[0] = i;
      let rowMin = cur[0];
      for (let j = 1; j <= b.length; j++) {
        const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        if (cur[j] < rowMin) rowMin = cur[j];
      }
      if (rowMin > max) return max + 1;
      [prev, cur] = [cur, prev];
    }

    return prev[b.length];
  }

  // How many typos we forgive, based on query length
  static fuzzyBudget(length) {
    if (length < 4) return 0;
    if (length < 7) return 1;
    return 2;
  }

  /*
    Typed term -> canonical SKUs.
      exact - normalized forms match exactly
      loose - prefix match or typo
  */
  resolve(term) {
    const q = SkuIndex.normalize(term);
    if (!this.rows || !this.rows.length || q.length < 2) return { exact: [], loose: [] };

    const exact = [];
    const prefix = [];
    const fuzzy = [];
    const budget = SkuIndex.fuzzyBudget(q.length);

    for (const row of this.rows) {
      if (row.n === q) {
        exact.push(row.sku);
      } else if (row.n.startsWith(q)) {
        prefix.push(row);
      } else if (budget && SkuIndex.distance(q, row.n, budget) <= budget) {
        fuzzy.push(row);
      }
    }

    prefix.sort((a, b) => a.n.length - b.n.length);
    fuzzy.sort((a, b) => a.n.length - b.n.length);

    const loose = [...prefix, ...fuzzy].slice(0, 4).map((r) => r.sku);
    return { exact, loose };
  }

  // Single best candidate - used when rewriting q
  best(term) {
    const { exact, loose } = this.resolve(term);
    return exact[0] || loose[0] || null;
  }
}

const skuIndex = new SkuIndex();

/* =============================================================
   Full search results page (/search)
   ============================================================= */

// Build a results URL for the given term, keeping other params
function buildSearchUrl(term, params) {
  const search = params || new URLSearchParams();
  search.set('q', term);
  search.set('options[prefix]', 'last');
  search.set('sku_resolved', '1'); // guard against redirect loops

  const base = (window.routes && routes.search_url) || `${SkuIndex.root()}search`;
  return `${base}?${search.toString()}`;
}

/*
  Direct visit: /search?q=5084s1 was rendered empty server-side.
  Translate the query and replace the URL with the canonical one.
*/
function resolveSearchPage() {
  if (!/\/search/.test(window.location.pathname)) return;

  const params = new URLSearchParams(window.location.search);
  const q = (params.get('q') || '').trim();

  if (!q || params.get('sku_resolved')) return;
  if (!SkuIndex.isSkuLike(q)) return;

  // This class is only present when the result set is empty
  if (!document.querySelector('.template-search--empty')) return;

  skuIndex.load().then(() => {
    const canonical = skuIndex.best(q);
    if (!canonical || canonical === q) return;

    window.location.replace(buildSearchUrl(canonical, params));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', resolveSearchPage);
} else {
  resolveSearchPage();
}

/* =============================================================
   Dropdown
   ============================================================= */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    // 300 -> 150: suggestions appear almost from the first character
    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 150).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  /*
    Submit: rewrite q to the canonical SKU BEFORE navigating.
    Otherwise 5084s1 reaches /search and Shopify returns nothing.
  */
  onFormSubmit(event) {
    const term = this.getQuery();

    if (!term.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    if (!SkuIndex.isSkuLike(term)) return; // plain text - leave untouched

    event.preventDefault();

    skuIndex.load().then(() => {
      const canonical = skuIndex.best(term) || term;
      window.location.href = buildSearchUrl(canonical, new URLSearchParams());
    });
  }

  onFocus() {
    // Warm the index up front so it is ready by the first character
    skuIndex.load();

    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  /* ---------------------------------------------------------
     Query chain
     --------------------------------------------------------- */

  isSkuLike(term) {
    return SkuIndex.isSkuLike(term);
  }

  /*
    From exact to broad. The exact flag decides whether the
    "similar products" notice is shown.

      5084z1 -> [ {5084z1, exact}, {5-08-4-s1, loose}, {5-08-4, loose} ]
  */
  buildQueryChain(searchTerm) {
    const chain = [{ q: searchTerm, exact: true }];

    if (!this.isSkuLike(searchTerm)) return chain;

    const push = (q, exact) => {
      if (q && q.length >= 2 && !chain.some((c) => c.q.toLowerCase() === q.toLowerCase())) {
        chain.push({ q, exact });
      }
    };

    // Index translation: typed term -> canonical SKU
    const { exact, loose } = skuIndex.resolve(searchTerm);
    exact.forEach((sku) => push(sku, true));
    loose.forEach((sku) => push(sku, false));

    // Trim the tail - siblings from the same series
    const parts = searchTerm.split(/[-_/\s.]+/).filter(Boolean);
    for (let i = parts.length - 1; i >= 1; i--) {
      push(parts.slice(0, i).join('-'), false);
    }

    return chain;
  }

  fetchQuery(term) {
    const fields = 'title,product_type,variants.title,vendor,tag,variants.sku';

    const url =
      `${routes.predictive_search_url}` +
      `?q=${encodeURIComponent(term)}` +
      `&${encodeURIComponent('resources[type]')}=product` +
      `&${encodeURIComponent('resources[limit]')}=10` +
      `&${encodeURIComponent('resources[options][unavailable_products]')}=last` +
      `&${encodeURIComponent('resources[options][fields]')}=${encodeURIComponent(fields)}` +
      `&section_id=predictive-search`;

    return fetch(url).then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.text();
    });
  }

  extractSection(text) {
    const section = new DOMParser()
      .parseFromString(text, 'text/html')
      .querySelector('#shopify-section-predictive-search');
    return section ? section.innerHTML : '';
  }

  hasProducts(markup) {
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    return (
      doc.querySelectorAll(
        '#predictive-search-results-list li[id^="predictive-search-option-"]:not(#predictive-search-option-search-keywords)'
      ).length > 0
    );
  }

  /*
    Notice text is read from data-similar-text so it can be
    translated via locale files:
      <predictive-search data-similar-text="{{ 'templates.search.similar' | t }}">
  */
  addSimilarNotice(markup) {
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const list = doc.querySelector('#predictive-search-results-list');
    if (!list) return markup;

    const heading = doc.querySelector('#predictive-search-products');
    if (heading) heading.remove();

    const notice = doc.createElement('p');
    notice.className = 'predictive-search__heading text-body caption-with-letter-spacing';
    notice.textContent = this.dataset.similarText || SKU_SIMILAR_TEXT_FALLBACK;
    list.parentNode.insertBefore(notice, list);

    return doc.body.innerHTML;
  }

  runChain(chain, searchTerm, i = 0) {
    if (i >= chain.length) {
      // Nothing found - return the response for the original term as is
      return this.fetchQuery(searchTerm).then((text) => this.extractSection(text));
    }

    return this.fetchQuery(chain[i].q).then((text) => {
      const markup = this.extractSection(text);

      if (this.hasProducts(markup)) {
        return chain[i].exact ? markup : this.addSimilarNotice(markup);
      }

      return this.runChain(chain, searchTerm, i + 1);
    });
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // The index is needed before building the chain, but it loads once per session
    skuIndex
      .load()
      .then(() => this.runChain(this.buildQueryChain(searchTerm), searchTerm))
      .then((resultsMarkup) => {
        // The customer may have typed further while we were on the network
        if (this.getQuery() !== searchTerm) return;

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(countEl ? countEl.textContent : '');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);